import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { SearchInput } from 'foundations-components/transitional/components/SearchInput';
import { ScrollContainer } from 'foundations-components/transitional/components/ScrollContainer';
import { HR } from 'foundations-components/transitional/components/HR';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { TruncateString } from 'foundations-components/transitional/components/TruncateString';
import { View } from 'foundations-components/transitional/components/View';
import styled from 'foundations-theming/utils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getFilteredCharmap, getFrequentlyUsed, setFrequentlyUsed } from '../../utils/charmap';
import { memoize } from '../../utils/functions';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const CharmapScrollContainer = styled.div.withConfig({
  displayName: "CharmapInsertPopoverBody__CharmapScrollContainer",
  componentId: "sc-1qdcgsf-0"
})(["height:200px !important;overflow-y:scroll !important;position:relative !important;line-height:initial;margin-top:6px;margin-bottom:10px;margin-left:20px;padding-right:15px;user-select:none;"]);
const CategoryLabel = styled.div.withConfig({
  displayName: "CharmapInsertPopoverBody__CategoryLabel",
  componentId: "sc-1qdcgsf-1"
})(({
  theme
}) => ({
  backgroundColor: theme.color['fill-surface-default'],
  color: theme.color['text-core-subtle'],
  fontSize: theme.typography['detail-100'].fontSize,
  lineHeight: theme.typography['detail-100'].lineHeight,
  padding: `${theme.space['100']} 0 ${theme.space['250']}`,
  position: 'sticky',
  top: 0,
  zIndex: 2
}));
const CharacterItem = styled.span.withConfig({
  displayName: "CharmapInsertPopoverBody__CharacterItem",
  componentId: "sc-1qdcgsf-2"
})(({
  theme
}) => ({
  cursor: 'pointer',
  overflow: 'hidden',
  inlineSize: '36px',
  blockSize: '36px',
  borderRadius: theme.borderRadius['full'],
  fontSize: theme.typography['heading-300'].fontSize,
  fontWeight: theme.typography['heading-500'].fontWeight,
  lineHeight: theme.typography['heading-600'].lineHeight,
  textAlign: 'center',
  display: 'inline-block',
  zIndex: 1,
  transition: 'background-color 0.2s',
  '&:hover': {
    backgroundColor: theme.color['fill-field-hover']
  }
}));
const SelectedCharacter = styled.div.withConfig({
  displayName: "CharmapInsertPopoverBody__SelectedCharacter",
  componentId: "sc-1qdcgsf-3"
})(({
  theme
}) => ({
  fontSize: theme.typography['heading-100'].fontSize,
  fontWeight: theme.typography['heading-100'].fontWeight,
  textAlign: 'center',
  inlineSize: '24px',
  overflow: 'visible'
}));
const SelectedCharacterName = styled.div.withConfig({
  displayName: "CharmapInsertPopoverBody__SelectedCharacterName",
  componentId: "sc-1qdcgsf-4"
})(({
  theme
}) => ({
  marginInlineStart: theme.space['200'],
  marginInlineEnd: theme.space['300'],
  fontSize: theme.typography['detail-100'].fontSize
}));
const memoizedGetFilteredCharmap = memoize(getFilteredCharmap);
const renderCategorizedCharmap = (charmap = {}, handleMouseOver, handleInsert) => Object.entries(charmap).map(([category, entries]) => {
  const Header = /*#__PURE__*/_jsx(CategoryLabel, {
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: `RichTextLib.charmap.categories.${category}`
    })
  });
  let Items;
  if (!entries.length) {
    Items = [/*#__PURE__*/_jsx("div", {
      children: `${I18n.text('RichTextLib.charmap.noResultsFound')} ☹`
    }, "empty-results")];
  } else {
    Items = entries.map(({
      charCode,
      label
    } = {}) => charCode && /*#__PURE__*/_jsx(CharacterItem, {
      onMouseOver: () => handleMouseOver(charCode, label),
      onClick: () => {
        handleInsert(charCode);
      },
      children: String.fromCharCode(charCode)
    }, `char-code-${charCode}`));
  }
  const key = `charmap-category-${category}`;
  return /*#__PURE__*/_jsxs("div", {
    "data-test-id": key,
    children: [Header, Items]
  }, key);
});
export default function CharmapInsertPopoverBody(props) {
  const {
    onInsert,
    storeHandlers: {
      init,
      getter,
      setter
    } = {}
  } = props;
  const [selectedChar, setSelectedChar] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [frequentlyUsedCharmap, setFrequentlyUsedCharmap] = useState({});
  const searchInputRef = useRef(null);
  const isMounted = useRef(false);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      return;
    };
  }, []);

  // Initialize the charmap store on mount
  useEffect(() => {
    if (init) {
      init(() => {
        if (isMounted.current) {
          setFrequentlyUsedCharmap(getFrequentlyUsed(getter));
        }
      });
    }
    // Focus on search input
    if (searchInputRef.current) searchInputRef.current.focus();
  }, [init, getter]);
  const handleSearchChange = ({
    target: {
      value
    }
  }) => {
    setSearchTerm(value);
    setSelectedChar({});
  };
  const handleMouseOver = (charCode, name) => {
    setSelectedChar({
      char: String.fromCharCode(charCode),
      name
    });
  };
  const handleInsert = useCallback(charCode => {
    onInsert(charCode);
    setFrequentlyUsed(setter, charCode);
    setFrequentlyUsedCharmap(getFrequentlyUsed(getter));
    // Refocus on the search input
    if (searchInputRef.current) searchInputRef.current.focus();
  }, [onInsert, searchInputRef, setter, getter]);
  const preventDefaultClick = evt => {
    // Don't allow the UIDropdown to default close on click
    if (evt.nativeEvent && evt.nativeEvent.stopImmediatePropagation) {
      evt.nativeEvent.stopImmediatePropagation();
    }
  };
  const FrequentlyUsedGrid = useMemo(() => {
    return !searchTerm && renderCategorizedCharmap(frequentlyUsedCharmap, handleMouseOver, handleInsert);
  }, [searchTerm, frequentlyUsedCharmap, handleInsert]);

  // Only regenerate charmap grid when the searchTerm has changed
  const CharmapGrid = useMemo(() => {
    const charmap = memoizedGetFilteredCharmap(searchTerm);
    return renderCategorizedCharmap(charmap, handleMouseOver, handleInsert);
  }, [searchTerm, handleInsert]);
  return /*#__PURE__*/_jsxs(View, {
    "data-test-id": "charmap-insert-popover-body",
    inlineSize: "294px",
    onClick: preventDefaultClick,
    children: [/*#__PURE__*/_jsx(FormControl, {
      "aria-label": "search",
      padding: "500",
      children: /*#__PURE__*/_jsx(SearchInput, {
        "data-test-id": "charmap-search-input",
        placeholder: I18n.text('RichTextLib.charmap.searchPlaceholder'),
        icon: false,
        clearable: false,
        onChange: handleSearchChange,
        inputRef: searchInputRef,
        autoFocus: true,
        autoFocusPreventScroll: true
      })
    }), /*#__PURE__*/_jsx(HR, {
      distance: "flush"
    }), /*#__PURE__*/_jsxs(ScrollContainer, {
      "data-test-id": "charmap-scroll-container",
      scrollDirection: "vertical",
      maxHeight: 200,
      size: 0,
      ScrollContainer: CharmapScrollContainer,
      children: [FrequentlyUsedGrid, CharmapGrid]
    }), /*#__PURE__*/_jsx(HR, {
      distance: "flush"
    }), /*#__PURE__*/_jsxs(TFlex, {
      marginBlock: "200",
      inlineSize: "100%",
      blockSize: "24px",
      paddingInlineStart: "400",
      children: [/*#__PURE__*/_jsx(SelectedCharacter, {
        children: selectedChar.char || ''
      }), /*#__PURE__*/_jsx(SelectedCharacterName, {
        children: /*#__PURE__*/_jsx(TruncateString, {
          children: selectedChar.name || ''
        })
      })]
    })]
  });
}